import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { ExpandMore, Psychology, AutoAwesome } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface AIReasoningAccordionProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  htmlContent?: string;
  panelId?: string;
}

const AIReasoningAccordion = ({
  expanded,
  onChange,
  htmlContent,
  panelId = 'ai-reasoning',
}: AIReasoningAccordionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{
        mt: 2,
        borderRadius: 1.2,
        overflow: 'hidden',
        boxShadow: 'none',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '16px 0',
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore
            sx={{
              color: 'white',
              transition: 'transform 0.3s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        aria-controls={`${panelId}-content`}
        id={`${panelId}-header`}
        sx={{
          minHeight: 48,
          padding: '0 16px',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          },
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Psychology sx={{ fontSize: 18, color: theme.palette.common.white }} />
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.common.white,
                fontWeight: 600,
                letterSpacing: '0.3px',
              }}
            >
              {t('whatsapp.iaReasoning.viewReasoning')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.common.white,
                fontSize: '0.75rem',
              }}
            >
              {expanded ? t('whatsapp.iaReasoning.hideDetails') : t('whatsapp.iaReasoning.viewCompleteAnalysis')}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: 0,
          background: `linear-gradient(145deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
        }}
      >
        <Box
          sx={{
            p: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.light}30 50%, transparent 100%)`,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 0.8,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
              }}
            >
              <AutoAwesome sx={{ fontSize: 16, color: theme.palette.common.white }} />
            </Box>
            <Typography variant="subtitle2" color="text.primary">
              {t('whatsapp.iaReasoning.contextAnalysis')}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.2,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                {htmlContent && (
                  <Box
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6,
                      mb: 1,
                      '& h4': {
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        mb: 1,
                        mt: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:first-of-type': { mt: 0 },
                      },
                      '& p': {
                        margin: '0.5rem 0',
                        fontSize: '0.875rem',
                        color: theme.palette.text.secondary,
                      },
                      '& ul, & ol': {
                        margin: '0.5rem 0',
                        paddingLeft: '1.5rem',
                      },
                      '& li': {
                        fontSize: '0.875rem',
                        marginBottom: '0.25rem',
                        color: theme.palette.text.secondary,
                      },
                      '& .decision-highlight': {
                        fontWeight: 600,
                        color: theme.palette.success.main,
                        backgroundColor: `${theme.palette.success.light}20`,
                        padding: '0.5rem',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.success.light}`,
                      },
                      '& .reasoning-section': {
                        marginBottom: '1rem',
                        '&:last-child': { marginBottom: 0 },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AIReasoningAccordion;
